import qs from "qs";
import { DEFAULT_ROOT_MEDIA_FOLDER_NAME } from "dash-common";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo } from "react";

import {
  FilterContext,
  PaginationContext,
  MarketingFiles as MarketingFilesComponent,
} from "dash-ui";

import {
  GetPropertyByIdQuery,
  useGetAllFilesInPropertyQuery,
  useGetAllFoldersInPropertyQuery,
  useGetTopLevelFoldersInPropertyQuery,
} from "gql/generated.graphql";
import { downloadAllMediaInProperty, downloadMedia } from "common/helpers/downloadMedia";

import { RoutesPath } from "flow-components/routes/routes";

const MarketingFiles = ({
  property,
  allowDownloads,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
  allowDownloads?: boolean;
}) => {
  const propertyId = property._id;
  const navigate = useNavigate();
  const location = useLocation();
  const { state: paginationState, getRefetchProps, setTotal } = useContext(PaginationContext);

  const { state: filterState } = useContext(FilterContext);
  const { limit } = getRefetchProps(paginationState);

  const { data: foldersData, loading: foldersLoading } = useGetTopLevelFoldersInPropertyQuery({
    variables: { propertyId: property._id },
  });

  const { folderId: parentFolderId } = useMemo(
    () => qs.parse(location.search.slice(1)),
    [location]
  );

  const tabsFolders = useMemo(() => foldersData?.getTopLevelFoldersInProperty || [], [foldersData]);

  const mediaFolderId = useMemo(
    () => parentFolderId || (tabsFolders.length > 0 ? tabsFolders[0]._id : ""),
    [parentFolderId, tabsFolders]
  );

  const { data: innerFoldersData, loading: innerFoldersLoading } = useGetAllFoldersInPropertyQuery({
    variables: {
      propertyId: property._id,
      mediaFolderId,
    },
    skip: !!tabsFolders.some(
      (item) => item._id === mediaFolderId && item.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME
    ),
  });

  const innerFolders = useMemo(
    () => innerFoldersData?.getAllFoldersInProperty || [],
    [innerFoldersData]
  );

  const allFilesListVariables = useMemo(
    () => ({
      pagination: { limit },
      filter: {
        propertyId,
        mediaFolderIds:
          filterState.foldersIds?.length > 0
            ? filterState.foldersIds
            : [mediaFolderId, ...innerFolders.map((folder) => folder._id)],
      },
    }),
    [limit, propertyId, filterState.foldersIds, mediaFolderId, innerFolders]
  );

  const {
    data: allFilesInProperty,
    loading: childFoldersFilesLoading,
    fetchMore: fetchMoreChildFoldersFiles,
  } = useGetAllFilesInPropertyQuery({
    variables: allFilesListVariables,
    fetchPolicy: "network-only",
  });

  const { allFilesCollection, allFilesCollectionTotal } = useMemo(
    () => ({
      allFilesCollection: allFilesInProperty?.getAllFilesInProperty.filesCollection || [],
      allFilesCollectionTotal: allFilesInProperty?.getAllFilesInProperty.filesTotalCount || 0,
    }),
    [allFilesInProperty]
  );

  const onDownloadSelectedClick = (selected: Record<string, boolean>) => {
    const toDownload = Object.keys(selected).filter((key) => selected[key]);

    if (toDownload.length) {
      downloadMedia(toDownload);
    } else {
      downloadAllMediaInProperty(property._id);
    }
  };

  useEffect(() => {
    setTotal(allFilesCollectionTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFilesCollectionTotal]);

  const files = useMemo(
    () =>
      allFilesCollection.map((item) => {
        const file = item.files[0];

        return {
          _id: item._id,
          name: file.name,
          size: item.sizeInBytes,
          publicUrl: file.publicUrl,
          thumbnailFileUrl: file.thumbnailFileUrl,
          type: file.type,
        };
      }),
    [allFilesCollection]
  );

  return (
    <MarketingFilesComponent
      tabs={tabsFolders.map((item) => ({
        label: item.folderName,
        key: item._id,
      }))}
      files={files as any}
      folders={innerFolders as any}
      activeTab={parentFolderId as string}
      isPortfolioView={true}
      onDownloadClick={onDownloadSelectedClick}
      loading={foldersLoading || childFoldersFilesLoading || innerFoldersLoading}
      fetchMoreProps={{
        fetchMore: fetchMoreChildFoldersFiles,
        variables: allFilesListVariables,
        queryName: "getAllFilesInProperty",
        collectionName: "filesCollection",
      }}
      setActiveTab={(tab) =>
        navigate(RoutesPath.PropertyPreview.replace(":id", propertyId) + `?folderId=${tab}`)
      }
      allowDownloads={allowDownloads}
    />
  );
};

export default MarketingFiles;
